// src/Components/Descargas/downloadNativeFile.js
//
// Descarga un fichero remoto en la app nativa (Capacitor Filesystem +
// Share). Reescrito (ver informe de exportación): antes se hacía
// `window.alert('Fichero guardado en el dispositivo: X')` en cuanto fallaba
// Share.share(), aunque:
//   1. El fichero solo estaba en Directory.Documents, una carpeta privada
//      de la app que en Android (almacenamiento con ámbito, API 30+) no es
//      accesible desde "Archivos" sin pasar por el propio share sheet.
//   2. `Filesystem`/`Share` pueden estar en package.json y en el código
//      fuente sin estar realmente registrados en el binario YA INSTALADO
//      (mismo problema ya conocido y resuelto para @capacitor/browser con
//      canUseSystemBrowserPlugin en EncuestasComponent.js).
//   3. Cancelar la hoja de compartir no es un error: no debe mostrarse
//      ningún mensaje de "guardado" ni de error en ese caso.
//
// Ahora: se comprueba isPluginAvailable ANTES de intentar nada, nunca se
// afirma un guardado que no se puede confirmar, y si algo no está
// disponible se cae a abrir la versión web (que sí genera el fichero real)
// en vez de fingir un éxito nativo.

const sanitizeFileName = (name, extension) => {
  const ext = String(extension || '').replace(/^\.+/, '').toLowerCase() || 'bin';
  const cleaned = String(name || `documento.${ext}`)
    .replace(/[<>:"/\\|?* -]/g, '_')
    .trim();

  if (!cleaned) return `documento.${ext}`;

  return cleaned.toLowerCase().endsWith(`.${ext}`) ? cleaned : `${cleaned}.${ext}`;
};

const isNativeApp = () =>
  typeof window !== 'undefined' &&
  Boolean(window.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());

// Mismo patrón que canUseSystemBrowserPlugin (EncuestasComponent.js): estar
// dentro de Capacitor no basta, hay que comprobar que el plugin nativo esté
// REALMENTE registrado en el binario instalado.
const isPluginAvailable = (pluginName) =>
  Boolean(window?.Capacitor?.isPluginAvailable && window.Capacitor.isPluginAvailable(pluginName));

const openInBrowser = (url) => {
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (_) { /* noop */ }
};

/**
 * @param {Object} opts
 * @param {string} opts.url        URL absoluta que devuelve el fichero real
 *                                 (Content-Type/Content-Disposition
 *                                 correctos), nunca una página HTML.
 * @param {string} opts.fileName   Nombre base sugerido.
 * @param {string} [opts.extension='bin']
 * @param {Function} [opts.onError]  Callback (err) si la descarga nativa falla de verdad.
 *
 * @returns {Promise<{mode: string, uri?: string, error?: Error, reason?: string}>}
 *
 * Modos:
 *   - 'browser'      : contexto web puro (no app nativa); se abrió la URL directamente.
 *   - 'fallback-web' : app nativa pero Filesystem/Share no disponibles en
 *                      este binario; se abrió la URL como en 'browser'.
 *   - 'shared'       : Filesystem + Share funcionaron; el sistema YA mostró
 *                      la hoja nativa de compartir/guardar (esa hoja ES la
 *                      confirmación, aquí no se afirma nada más).
 *   - 'error'        : fallo real durante la descarga/escritura nativa.
 */
export async function downloadNativeFile({ url, fileName, extension = 'bin', onError }) {
  if (!isNativeApp()) {
    openInBrowser(url);
    return { mode: 'browser' };
  }

  if (!isPluginAvailable('Filesystem')) {
    console.warn('[downloadNativeFile] Plugin Filesystem no registrado en este binario; uso la versión web.');
    openInBrowser(url);
    return { mode: 'fallback-web', reason: 'filesystem-unavailable' };
  }

  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');

    const safeFileName = sanitizeFileName(fileName, extension);
    const relativePath = `descargas/${Date.now()}-${safeFileName}`;

    await Filesystem.downloadFile({
      url,
      path: relativePath,
      directory: Directory.Documents,
      recursive: true,
    });

    const { uri } = await Filesystem.getUri({
      directory: Directory.Documents,
      path: relativePath,
    });

    if (!isPluginAvailable('Share')) {
      // El fichero SÍ está escrito, pero sin Share no hay forma fiable de
      // que el usuario lo encuentre (carpeta privada de la app). Mejor
      // ofrecer la descarga web, que el usuario sí puede gestionar con el
      // propio navegador/gestor de descargas del sistema.
      console.warn('[downloadNativeFile] Plugin Share no registrado en este binario; uso la versión web.');
      openInBrowser(url);
      return { mode: 'fallback-web', reason: 'share-unavailable', uri };
    }

    const { Share } = await import('@capacitor/share');
    await Share.share({
      title: safeFileName,
      text: `Guardar ${safeFileName}`,
      url: uri,
      dialogTitle: `Guardar ${safeFileName}`,
    });

    // Si Share.share() resuelve, el sistema ya mostró (y el usuario ya
    // cerró) la hoja nativa de compartir/guardar. Esa hoja ES la
    // confirmación: no hay un segundo mensaje de "guardado" que mostrar, y
    // desde aquí no se puede saber con certeza qué opción eligió el
    // usuario dentro de ella (ni hace falta saberlo).
    return { mode: 'shared', uri };
  } catch (error) {
    console.error('[downloadNativeFile] Descarga nativa fallida:', error);
    if (typeof onError === 'function') {
      try { onError(error); } catch (_) { /* noop */ }
    }
    return { mode: 'error', error };
  }
}
